import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../api";
import { WalletLink } from "../components/WalletLink";
import { QuarterSelector, QUARTER_PRESETS } from "../components/QuarterSelector";
import { QuarterlyBreakdownTable } from "../components/QuarterlyBreakdownTable";
import { AirdropLeaderboard } from "../components/AirdropLeaderboard";
import { useAirdrops } from "../hooks/useAirdrops";
import { Skeleton } from "../components/Skeleton";

type LeaderboardType = "points" | "referrers" | "quarterly" | "airdrops";

const TABS: { id: LeaderboardType; label: string }[] = [
  { id: "points", label: "Points" },
  { id: "referrers", label: "Referrers" },
  { id: "quarterly", label: "Quarterly points" },
  { id: "airdrops", label: "$CARDS airdrops" },
];

export function Leaderboards() {
  const [activeTab, setActiveTab] = useState<LeaderboardType>("points");
  const [limit, setLimit] = useState(25);
  const defaultPreset = QUARTER_PRESETS[0]!;
  const [quarterlyPeriod, setQuarterlyPeriod] = useState({
    from: defaultPreset.from,
    to: defaultPreset.to,
    id: defaultPreset.id,
  });

  // $CARDS airdrops - used to show "$CARDS received" next to quarterly points
  const airdropsQuery = useAirdrops();
  const airdropForQuarter = airdropsQuery.data?.data.find(
    (a) => a.quarterId === quarterlyPeriod.id
  );

  // Fetch points leaderboard
  const pointsQuery = useQuery({
    queryKey: ["leaderboard-points-full", limit],
    queryFn: async () => {
      const { data, error } = await api.leaderboard.points.get({
        query: { limit },
      });
      if (error) throw error;
      return data;
    },
    enabled: activeTab === "points",
  });

  // Fetch referrers leaderboard
  const referrersQuery = useQuery({
    queryKey: ["leaderboard-referrers-full", limit],
    queryFn: async () => {
      const { data, error } = await api.leaderboard.referrers.get({
        query: { limit },
      });
      if (error) throw error;
      return data;
    },
    enabled: activeTab === "referrers",
  });

  // Only the leader gets the gold; everyone else is neutral
  const getRankClass = (index: number) =>
    index === 0
      ? "bg-yellow-400/15 text-yellow-400"
      : "bg-gray-700/70 text-gray-400";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title">Leaderboards</h1>
        <p className="section-hint mt-1">
          Who's leading in points, referrals and $CARDS airdrops.
        </p>
      </div>

      {/* Tabs + limit */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="seg">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`seg-btn ${activeTab === tab.id ? "seg-btn-on" : ""}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Limit selector (not shown for quarterly / airdrop tabs) */}
        {activeTab !== "quarterly" && activeTab !== "airdrops" && (
          <div className="flex items-center gap-2">
            <span className="text-gray-400 text-sm">Show</span>
            <div className="seg">
              {[10, 25, 50, 100].map((l) => (
                <button
                  key={l}
                  onClick={() => setLimit(l)}
                  className={`seg-btn num ${limit === l ? "seg-btn-on" : ""}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Points Leaderboard */}
      {activeTab === "points" && (
        <section className="card">
          <h2 className="section-title mb-4">Points leaders</h2>

          {pointsQuery.isLoading && (
            <Skeleton kind="table" rows={10} />
          )}

          {pointsQuery.error && (
            <p className="text-red-400">
              Couldn't load the leaderboard. {String(pointsQuery.error)}
            </p>
          )}

          {pointsQuery.data?.data && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="table-head">
                    <th className="pb-3 pr-4 w-16 font-medium">Rank</th>
                    <th className="pb-3 pr-4 font-medium">Wallet</th>
                    <th className="pb-3 pr-4 text-right font-medium">Total points</th>
                    <th className="pb-3 pr-4 text-right font-medium">Primary</th>
                    <th className="pb-3 pr-4 text-right font-medium">Secondary</th>
                    <th className="pb-3 text-right font-medium">Bonus</th>
                  </tr>
                </thead>
                <tbody>
                  {pointsQuery.data.data.map((wallet, index) => (
                    <tr key={wallet.id} className="table-row">
                      <td className="py-3 pr-4">
                        <span
                          className={`num w-7 h-7 flex items-center justify-center rounded-full text-xs font-semibold ${getRankClass(
                            index
                          )}`}
                        >
                          {index + 1}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        <WalletLink address={wallet.walletAddress} />
                      </td>
                      <td className="py-3 pr-4 text-right num font-semibold text-gray-50">
                        {wallet.totalPoints?.toLocaleString()}
                      </td>
                      <td className="py-3 pr-4 text-right num text-gray-300">
                        {wallet.primaryPoints?.toLocaleString()}
                      </td>
                      <td className="py-3 pr-4 text-right num text-gray-300">
                        {wallet.secondaryPoints?.toLocaleString()}
                      </td>
                      <td className="py-3 text-right num text-gray-300">
                        {wallet.bonusPoints?.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Referrers Leaderboard */}
      {activeTab === "referrers" && (
        <section className="card">
          <h2 className="section-title mb-4">Top referrers</h2>

          {referrersQuery.isLoading && (
            <Skeleton kind="table" rows={10} />
          )}

          {referrersQuery.error && (
            <p className="text-red-400">
              Couldn't load the leaderboard. {String(referrersQuery.error)}
            </p>
          )}

          {referrersQuery.data?.data && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="table-head">
                    <th className="pb-3 pr-4 w-16 font-medium">Rank</th>
                    <th className="pb-3 pr-4 font-medium">Wallet</th>
                    <th className="pb-3 pr-4 font-medium">Refcode</th>
                    <th className="pb-3 text-right font-medium">Referred wallets</th>
                  </tr>
                </thead>
                <tbody>
                  {referrersQuery.data.data.map((referrer, index) => (
                    <tr key={referrer.id} className="table-row">
                      <td className="py-3 pr-4">
                        <span
                          className={`num w-7 h-7 flex items-center justify-center rounded-full text-xs font-semibold ${getRankClass(
                            index
                          )}`}
                        >
                          {index + 1}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        <WalletLink address={referrer.walletAddress} />
                      </td>
                      <td className="py-3 pr-4">
                        <span className="font-mono text-gray-200">
                          {referrer.refcode || "–"}
                        </span>
                      </td>
                      <td className="py-3 text-right num font-semibold text-gray-50">
                        {referrer.referredWallets?.length ?? 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Quarterly Points */}
      {activeTab === "quarterly" && (
        <section className="card">
          <h2 className="section-title mb-1">Quarterly points</h2>
          <p className="section-hint mb-4">
            Point gains and share per wallet during a quarter, useful for
            estimating airdrop allocation. When an airdrop for the selected
            quarter has been indexed, the $CARDS each wallet received is shown
            next to its points.
          </p>

          <div className="mb-6">
            <QuarterSelector
              onPeriodChange={(from, to, _label, id) =>
                setQuarterlyPeriod({ from, to, id })
              }
              initialQuarter={QUARTER_PRESETS[0]!.id}
            />
          </div>

          <QuarterlyBreakdownTable
            from={quarterlyPeriod.from}
            to={quarterlyPeriod.to}
            airdropId={airdropForQuarter?.id}
          />
        </section>
      )}

      {/* $CARDS Airdrops */}
      {activeTab === "airdrops" && (
        <section className="card">
          <h2 className="section-title mb-1 flex items-center gap-2">
            <svg
              className="w-5 h-5 text-yellow-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            $CARDS airdrops
          </h2>
          <p className="section-hint mb-4">
            How much $CARDS each wallet received per airdrop, next to the points
            it gained that quarter. Find wallets with similar points to compare
            allocations.
          </p>

          <AirdropLeaderboard />
        </section>
      )}
    </div>
  );
}
